import { useEffect, useState } from 'react';
import api from './api';

// Shared cache so the same attachment doesn't get fetched twice when it
// appears in multiple places (e.g. card thumbnail + drawer gallery +
// lightbox). Survives component unmounts because it lives at module
// scope. Object URLs created here are intentionally NOT revoked — the
// total page lifetime memory cost is bounded by the number of distinct
// attachments the user has seen, which is well under what a typical
// session would hit on a tasks board.
const blobCache = new Map(); // att.id -> objectURL
const inflight = new Map();  // att.id -> Promise<objectURL>

export async function fetchAttachmentBlobUrl(attId) {
  const cached = blobCache.get(attId);
  if (cached) return cached;
  const pending = inflight.get(attId);
  if (pending) return pending;

  const p = (async () => {
    const res = await api.get(`/tasks/attachments/${attId}/download`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    blobCache.set(attId, url);
    inflight.delete(attId);
    return url;
  })();
  inflight.set(attId, p);
  try { return await p; }
  catch (e) { inflight.delete(attId); throw e; }
}

/**
 * Returns a memoised blob URL for a task attachment, or null while it's
 * loading / on failure. Re-renders the consumer exactly once when the
 * URL becomes available.
 */
export function useAttachmentBlobUrl(attId) {
  const [url, setUrl] = useState(() => (attId ? blobCache.get(attId) || null : null));
  useEffect(() => {
    if (!attId) { setUrl(null); return; }
    const cached = blobCache.get(attId);
    if (cached) { setUrl(cached); return; }
    let cancelled = false;
    fetchAttachmentBlobUrl(attId)
      .then(u => { if (!cancelled) setUrl(u); })
      .catch(() => { /* render fallback icon */ });
    return () => { cancelled = true; };
  }, [attId]);
  return url;
}
